import Link from "next/link";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

type Variant = "light" | "primary" | "outline";
type Size = "default" | "sm";

interface CtaPillProps {
	href: string;
	children: ReactNode;
	variant?: Variant;
	size?: Size;
	className?: string;
	withArrow?: boolean;
	suffix?: ReactNode;
	onClick?: () => void;
}

const VARIANT_CLASSES: Record<Variant, string> = {
	light: "bg-white text-primary-dark shadow-md hover:bg-accent hover:text-white",
	primary: "bg-primary text-primary-foreground shadow-md hover:bg-primary-dark",
	outline: "border border-white/40 bg-transparent text-white hover:bg-white/10",
};

const SIZE_CLASSES: Record<Size, string> = {
	default: "px-7 py-4 text-[0.82rem] tracking-[0.18em]",
	sm: "px-5 py-2.5 text-[0.7rem] tracking-[0.16em]",
};

/**
 * Standardized CTA pill button — datasheet aesthetic, isolated hover.
 *
 * Click target is the pill ONLY (no parent group-hover); placing it inside a
 * larger card does not extend the click area.
 */
export function CtaPill({
	href,
	children,
	variant = "light",
	size = "default",
	className,
	withArrow = true,
	suffix,
	onClick,
}: CtaPillProps) {
	return (
		<Link
			href={href}
			onClick={onClick}
			className={cn(
				"group/cta inline-flex items-center gap-3 rounded-full font-medium uppercase transition-colors",
				VARIANT_CLASSES[variant],
				SIZE_CLASSES[size],
				className,
			)}
		>
			{children}
			{withArrow && (
				<span
					aria-hidden
					className={cn(
						"group-hover/cta:translate-x-1 transition-transform",
						size === "sm" ? "text-[1rem]" : "text-[1.2rem]",
					)}
				>
					→
				</span>
			)}
			{suffix}
		</Link>
	);
}
